import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Users, Phone, Star, AlertTriangle, CheckCircle, Clock, Shield } from "lucide-react";

const emptyDriver = {
  name: "", email: "", phone: "", license_number: "", license_expiry: "", license_class: "",
  msic_card_number: "", msic_card_expiry: "", white_card_number: "", white_card_expiry: "",
  medical_cert_expiry: "", dangerous_goods_license: "", dg_license_expiry: "",
  fatigue_accreditation: "", fatigue_expiry: "",
  status: "available", vehicle_assigned: "", rating: 5.0, total_trips: 0
};

// Helper to get expiry status color
const getExpiryStatus = (dateStr) => {
  if (!dateStr) return { color: "text-zinc-500", bg: "bg-zinc-500/10", status: "none", icon: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0) return { color: "text-red-500", bg: "bg-red-500/10", status: "expired", icon: AlertTriangle, days: daysUntil };
  if (daysUntil <= 7) return { color: "text-amber-500", bg: "bg-amber-500/10", status: "expiring", icon: Clock, days: daysUntil };
  return { color: "text-emerald-500", bg: "bg-emerald-500/10", status: "valid", icon: CheckCircle, days: daysUntil };
};

const ComplianceIndicator = ({ driver }) => {
  const fields = [
    driver.license_expiry,
    driver.msic_card_expiry,
    driver.white_card_expiry,
    driver.medical_cert_expiry,
    driver.dg_license_expiry,
    driver.fatigue_expiry
  ].filter(Boolean);
  
  if (fields.length === 0) return null;
  
  const statuses = fields.map(f => getExpiryStatus(f));
  const hasExpired = statuses.some(s => s.status === "expired");
  const hasExpiring = statuses.some(s => s.status === "expiring");
  
  if (hasExpired) return <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Has expired documents" />;
  if (hasExpiring) return <div className="w-2 h-2 rounded-full bg-amber-500" title="Documents expiring soon" />;
  return <div className="w-2 h-2 rounded-full bg-emerald-500" title="All documents valid" />;
};

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyDriver });

  const fetchDrivers = () => {
    axios.get(`${API}/drivers`).then(res => { setDrivers(res.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchDrivers(); }, []);

  const handleSave = async () => {
    if (!form.name) { toast.error("Driver name is required"); return; }
    try {
      if (editing) { await axios.put(`${API}/drivers/${editing.id}`, form); toast.success("Updated"); }
      else { await axios.post(`${API}/drivers`, form); toast.success("Driver added"); }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyDriver }); fetchDrivers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this driver?")) return;
    try { await axios.delete(`${API}/drivers/${id}`); toast.success("Deleted"); fetchDrivers(); } catch { toast.error("Failed"); }
  };

  const openEdit = (d) => { setEditing(d); setForm({ ...d }); setDialogOpen(true); };
  const openNew = () => { setEditing(null); setForm({ ...emptyDriver }); setDialogOpen(true); };

  return (
    <div data-testid="drivers-page">
      <PageHeader title="Drivers" subtitle={`${drivers.length} drivers`}>
        <button data-testid="create-driver-button" onClick={openNew}
          className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95">
          <Plus className="w-3.5 h-3.5" /> Add Driver
        </button>
      </PageHeader>

      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : drivers.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No drivers yet. Add your first driver.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {drivers.map((d) => (
              <div key={d.id} className="border border-zinc-800 bg-[#18181B] rounded-sm p-4 animate-fade-in hover:border-zinc-700 transition-colors" data-testid={`driver-card-${d.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center relative">
                      <span className="font-heading text-sm font-bold text-zinc-400">{d.name?.[0]?.toUpperCase()}</span>
                      <div className="absolute -top-0.5 -right-0.5">
                        <ComplianceIndicator driver={d} />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-bold text-white">{d.name}</h3>
                      {d.phone && <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{d.phone}</p>}
                    </div>
                  </div>
                  <StatusBadge status={d.status} />
                </div>

                <div className="space-y-1.5 text-xs text-zinc-400 mb-3">
                  {d.license_number && <p>License: <span className="font-mono text-zinc-300">{d.license_number}</span> {d.license_class && `(${d.license_class})`}</p>}
                  {d.license_expiry && (
                    <p className="flex items-center gap-1">
                      License Expires: <span className={`font-mono ${getExpiryStatus(d.license_expiry).color}`}>{d.license_expiry}</span>
                    </p>
                  )}
                  {d.msic_card_expiry && (
                    <p className="flex items-center gap-1">
                      MSIC: <span className={`font-mono ${getExpiryStatus(d.msic_card_expiry).color}`}>{d.msic_card_expiry}</span>
                    </p>
                  )}
                  {d.white_card_expiry && (
                    <p className="flex items-center gap-1">
                      White Card: <span className={`font-mono ${getExpiryStatus(d.white_card_expiry).color}`}>{d.white_card_expiry}</span>
                    </p>
                  )}
                  {d.email && <p className="truncate">{d.email}</p>}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-zinc-800/60">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs font-mono text-zinc-300">{d.rating}</span>
                    </div>
                    <span className="text-[10px] text-zinc-500">{d.total_trips} trips</span>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-driver-${d.id}`} onClick={() => openEdit(d)} className="text-zinc-500 hover:text-zinc-200 p-1"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-driver-${d.id}`} onClick={() => handleDelete(d.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#18181B] border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">{editing ? "Edit Driver" : "Add Driver"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Basic Info */}
            <div className="border-b border-zinc-800 pb-3">
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Personal Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Name *</Label>
                  <Input data-testid="driver-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Phone</Label>
                  <Input data-testid="driver-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                  <Input data-testid="driver-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                  <select data-testid="driver-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                    className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                    <option value="available">Available</option>
                    <option value="on_trip">On Trip</option>
                    <option value="off_duty">Off Duty</option>
                  </select>
                </div>
              </div>
            </div>

            {/* License Section */}
            <div className="border-b border-zinc-800 pb-3">
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-blue-500" />
                Driver License
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">License #</Label>
                  <Input data-testid="driver-license-input" value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Class</Label>
                  <select data-testid="driver-license-class" value={form.license_class} onChange={e => setForm({ ...form, license_class: e.target.value })}
                    className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                    <option value="">Select...</option>
                    <option value="C">C - Car</option>
                    <option value="LR">LR - Light Rigid</option>
                    <option value="MR">MR - Medium Rigid</option>
                    <option value="HR">HR - Heavy Rigid</option>
                    <option value="HC">HC - Heavy Combination</option>
                    <option value="MC">MC - Multi Combination</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">License Expiry</Label>
                  <Input data-testid="driver-license-expiry" type="date" value={form.license_expiry} onChange={e => setForm({ ...form, license_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
            </div>

            {/* Australian Compliance Section */}
            <div className="border-b border-zinc-800 pb-3">
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                Australian Transport Compliance
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">MSIC Card Number</Label>
                  <Input data-testid="driver-msic-number" value={form.msic_card_number} onChange={e => setForm({ ...form, msic_card_number: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="Maritime Security ID" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">MSIC Card Expiry</Label>
                  <Input data-testid="driver-msic-expiry" type="date" value={form.msic_card_expiry} onChange={e => setForm({ ...form, msic_card_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">White Card Number</Label>
                  <Input data-testid="driver-white-card-number" value={form.white_card_number} onChange={e => setForm({ ...form, white_card_number: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="Construction Induction" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">White Card Expiry</Label>
                  <Input data-testid="driver-white-card-expiry" type="date" value={form.white_card_expiry} onChange={e => setForm({ ...form, white_card_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Medical Certificate Expiry</Label>
                  <Input data-testid="driver-medical-expiry" type="date" value={form.medical_cert_expiry} onChange={e => setForm({ ...form, medical_cert_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">DG License Number</Label>
                  <Input data-testid="driver-dg-license" value={form.dangerous_goods_license} onChange={e => setForm({ ...form, dangerous_goods_license: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="Dangerous Goods" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">DG License Expiry</Label>
                  <Input data-testid="driver-dg-expiry" type="date" value={form.dg_license_expiry} onChange={e => setForm({ ...form, dg_license_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fatigue Accreditation</Label>
                  <Input data-testid="driver-fatigue-accred" value={form.fatigue_accreditation} onChange={e => setForm({ ...form, fatigue_accreditation: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="AFM/BFM Number" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fatigue Accreditation Expiry</Label>
                  <Input data-testid="driver-fatigue-expiry" type="date" value={form.fatigue_expiry} onChange={e => setForm({ ...form, fatigue_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Rating</Label>
                  <Input data-testid="driver-rating-input" type="number" min="0" max="5" step="0.1" value={form.rating} onChange={e => setForm({ ...form, rating: Number(e.target.value) })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-driver" onClick={handleSave} className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                {editing ? "Update" : "Add"} Driver
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
